<template>
  <div
    v-if="pageLoading"
    class="m-3"
  >
    <placeholder-content :is-load="true" />
  </div>
  <div class="m-3" v-else-if="!hasPermission">
    <access-denied />
  </div>
  <div
    v-else
  >
    <div class="d-flex border-bottom mb-3 justify-content-between align-items-end">
      <div class="overflow-auto hide-scrollbar flex-grow-1">
        <ul class="nav nav-custom flex-nowrap">
          <li
            v-for="t in tabs"
            :key="t.value"
            class="nav-item cursor-pointer"
            @click="setTab(t.value)"
          >
            <a class="nav-link text-nowrap" :class="{ 'active': filter.tab === t.value }">
              {{ $t(t.label) }}
              <span class="badge rounded-pill ms-1" :class="'bg-' + (t.color || 'primary')">
                {{ counts[t.value] || 0 }}
              </span>
            </a>
          </li>
        </ul>
      </div>
      <column-toggle
        :headers="allHeaders"
        storage-key="bills.columns"
        v-model="visibleColumns"
      />
    </div>
    <div class="d-flex justify-content-start mx-3 flex-column flex-sm-row flex-wrap">
      <div class="custom-input-group input-group input-group-sm me-sm-2 mb-3 w-auto">
        <input
          type="search"
          class="form-control"
          :placeholder="$t('search')+'...'"
          aria-label="Search"
          aria-describedby="search-addon"
          v-model.trim="params.search"
          @keyup.enter="fetchData"
        >
        <button
          class="input-group-text bg-white text-muted"
          type="button"
          @click="fetchData"
        >
          <i class="fas fa-search" />
        </button>
      </div>
      <div class="input-group input-group-sm me-sm-2 mb-3 w-auto date-range-input-group">
        <span class="input-group-text bg-white text-muted">
          <i class="fa-regular fa-calendar"/>
        </span>
        <date-range-picker
          ref="dateRangePicker"
          v-model="params.date_range"
          :date-range="params.date_range"
          :locale-data="dateRangeLocale"
          :ranges="false"
          :opens="'right'"
          :auto-apply="true"
          :show-dropdowns="true"
          :control-container-class="''"
        >
          <template v-slot:input="picker">
            <span v-if="picker.startDate && picker.endDate">
              {{ formatDate(picker.startDate) }} — {{ formatDate(picker.endDate) }}
            </span>
            <span v-else class="text-muted">
              {{ $t('Select date range') }}
            </span>
          </template>
        </date-range-picker>
        <button
          v-if="params.date_range && params.date_range.startDate && params.date_range.endDate"
          type="button"
          class="input-group-text"
          @click="clearDateRange"
        >
          <i class="fa-solid fa-xmark"/>
        </button>
      </div>
      <button
        type="button"
        class="btn btn-sm btn-outline-secondary text-nowrap position-relative me-sm-2 mb-3"
        @click="offcanvasFilter.open = true"
      >
        <i class="fa-solid fa-filter me-1" />
        {{ $t('Filters') }}
        <span v-if="activeFilterCount" class="badge bg-primary ms-1 rounded-pill">
          {{ activeFilterCount }}
        </span>
      </button>
      <per-page
        @per-page="setPerPage"
      />
    </div>
    <vue3-easy-data-table
      v-model:server-options="datatable.serverOptions"
      ref="dataTable"
      :headers="visibleHeaders"
      :items="datatable.items"
      :loading="datatable.loading"
      :server-items-length="datatable.total"
      :theme-color="'#515151'"
      :hide-footer="false"
      :hide-rows-per-page="true"
      :empty-message="$t('No Available Data')"
      :table-min-height="null"
      :body-item-class-name="'align-top py-1 px-2'"
      buttons-pagination
      table-class-name='customize-table'
    >
      <template #item-bill_no="item">
        <div class="fw-bold">{{ item.bill_no }}</div>
        <div class="text-muted small">{{ item.created_by }}</div>
      </template>

      <template #item-branch="item">
        {{ item.branch || '-' }}
      </template>

      <template #item-source="item">
        <span v-if="firstSource(item)" class="badge bg-light text-dark">
          <i :class="sourceIcon(firstSource(item))" class="me-1"/>
          {{ $t(sourceLabel(firstSource(item))) }}
        </span>
        <span v-else class="text-muted">-</span>
      </template>

      <template #item-ref="item">
        <div v-if="item.table_name">
          <i class="fa-solid fa-chair me-1 text-muted"/>
          <span class="fw-semibold">{{ item.table_name }}</span>
        </div>
        <div v-if="item.customer_name">
          <i class="fa-solid fa-user me-1 text-muted"/>{{ item.customer_name }}
        </div>
        <div v-if="!item.table_name && !item.customer_name" class="text-muted">
          {{ $t('Walk-in') }}
        </div>
      </template>

      <template #item-created_at="item">
        {{ formatDateTime(item.created_at) }}
      </template>

      <template #item-closed_at="item">
        <span v-if="item.payment_status === 'voided'">
          {{ formatDateTime(item.voided_at) }}
        </span>
        <span v-else-if="item.closed_at">
          {{ formatDateTime(item.closed_at) }}
        </span>
        <span v-else class="text-muted">-</span>
      </template>

      <template #item-payment_status="item">
        <span
          class="badge"
          :class="'bg-' + statusColor(item.payment_status)"
        >
          {{ $t(statusLabel(item.payment_status)) }}
        </span>
        <div
          v-if="toNumber(item.refunded_amount) > 0"
          class="text-muted small mt-1"
        >
          {{ $t('Refunded') }}: {{ $storeIndex.formatThaiCurrency(toNumber(item.refunded_amount)) }}
        </div>
      </template>

      <template #item-total="item">
        <div class="text-end fw-semibold">
          {{ $storeIndex.formatThaiCurrency(toNumber(item.total)) }}
        </div>
      </template>

      <template #item-tax_invoice="item">
        <!--
          ต้องมี icon-link custom-actions ครอบ ไม่งั้นปุ่มไม่ได้ขนาด 25x25 ของคอลัมน์ action
          (กฎอยู่ที่ public/assets/css/custom.css — ผูกกับ selector นี้เท่านั้น)
        -->
        <div v-if="hasTaxInvoice(item)" class="icon-link custom-actions d-flex align-items-center gap-1">
          <span class="badge bg-success-subtle text-success-emphasis text-nowrap">
            <i class="fa-solid fa-file-invoice me-1" />
            {{ item.tax_invoice_no }}
          </span>
          <popper
            :placement="'top'"
            :hover="true"
            :arrow="true"
            :disableClickAway="true"
            class="popper-default border-0 m-0"
          >
            <button
              type="button"
              class="btn btn-outline-danger btn-sm btn-icon"
              @click="quickVoidTaxInvoice(item)"
            >
              <i class="fa-solid fa-ban" />
            </button>
            <template #content>
              <div>{{ $t('Void Tax Invoice') }}</div>
            </template>
          </popper>
        </div>
        <!--
          ปุ่มไอคอน + tooltip เหมือนคอลัมน์ action อื่นทั้งตาราง — เดิมเป็นปุ่มมีข้อความเต็ม
          ซึ่งกว้างกว่าคอลัมน์อื่นมากจนตารางเสียจังหวะ
        -->
        <div v-else-if="canIssueTaxInvoice(item)" class="icon-link custom-actions">
          <popper
            :placement="'top'"
            :hover="true"
            :arrow="true"
            :disableClickAway="true"
            class="popper-default border-0 m-0"
          >
            <button
              type="button"
              class="btn btn-outline-primary btn-sm btn-icon"
              @click="openTaxInvoice(item)"
            >
              <i class="fa-solid fa-file-invoice" />
            </button>
            <template #content>
              <div>{{ $t('Issue Tax Invoice') }}</div>
            </template>
          </popper>
        </div>
        <span v-else class="text-muted">-</span>
      </template>

      <template #item-action="item">
        <div class="icon-link custom-actions">
          <popper
            :placement="'top'"
            :hover="true"
            :arrow="true"
            :disableClickAway="true"
            class="popper-default border-0 m-0"
          >
            <button
              type="button"
              class="btn btn-outline-primary btn-sm btn-icon"
              @click="openBill(item)"
            >
              <i class="fa-solid far fa-eye" />
            </button>
            <template #content>
              <div>{{ $t('View') }}</div>
            </template>
          </popper>

          <popper
            v-if="canCollect(item)"
            :placement="'top'"
            :hover="true"
            :arrow="true"
            :disableClickAway="true"
            class="popper-default border-0 m-0"
          >
            <button
              type="button"
              class="btn btn-outline-success btn-sm btn-icon"
              @click="openCollect(item)"
            >
              <i class="fa-solid fa-money-bill-wave" />
            </button>
            <template #content>
              <div>{{ $t('Collect Payment') }}</div>
            </template>
          </popper>

          <popper
            v-if="canRefund(item)"
            :placement="'top'"
            :hover="true"
            :arrow="true"
            :disableClickAway="true"
            class="popper-default border-0 m-0"
          >
            <button
              type="button"
              class="btn btn-outline-warning btn-sm btn-icon"
              @click="openRefund(item)"
            >
              <i class="fa-solid fa-rotate-left" />
            </button>
            <template #content>
              <div>{{ $t('Refund') }}</div>
            </template>
          </popper>

          <popper
            :placement="'top'"
            :hover="true"
            :arrow="true"
            :disableClickAway="true"
            class="popper-default border-0 m-0"
          >
            <button
              type="button"
              class="btn btn-outline-primary btn-sm btn-icon"
              :disabled="printingId === item.id"
              @click="quickPrint(item)"
            >
              <i :class="printingId === item.id ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-print'" />
            </button>
            <template #content>
              <div>{{ $t('Print Bill') }}</div>
            </template>
          </popper>

          <popper
            v-if="canVoid(item)"
            :placement="'top'"
            :hover="true"
            :arrow="true"
            :disableClickAway="true"
            class="popper-default border-0 m-0"
          >
            <button
              type="button"
              class="btn btn-outline-danger btn-sm btn-icon"
              @click="quickVoid(item)"
            >
              <i class="fa-solid fa-ban" />
            </button>
            <template #content>
              <div>{{ $t('Void Bill') }}</div>
            </template>
          </popper>
        </div>
      </template>
      <template #loading>
        <i class="fa-solid fa-spinner fa-spin fa-2xl" />
      </template>
    </vue3-easy-data-table>

    <offcanvas-info
      v-if="offcanvasInfo.open"
      ref="offcanvasInfoRef"
      :bill-id="offcanvasInfo.billId"
      @close="offcanvasInfo.open = false"
      @collect="openCollectById(offcanvasInfo.billId)"
      @refund="openRefundById(offcanvasInfo.billId)"
      @print="quickPrintById(offcanvasInfo.billId)"
      @void="quickVoidById(offcanvasInfo.billId)"
      @tax-invoice="openTaxInvoiceById(offcanvasInfo.billId)"
      @void-tax-invoice="quickVoidTaxInvoiceById(offcanvasInfo.billId)"
    />

    <modal-collect-payment
      v-if="collectModal.open"
      ref="collectModalRef"
      :bill="collectModal.bill"
      :payment-methods="collectModal.paymentMethods"
      @close="collectModal.open = false"
      @confirm="handleCollectConfirm"
    />

    <modal-refund
      v-if="refundModal.open"
      ref="refundModalRef"
      :bill="refundModal.bill"
      @close="refundModal.open = false"
      @confirm="handleRefundConfirm"
    />

    <modal-tax-invoice
      v-if="taxInvoiceModal.open"
      ref="taxInvoiceModalRef"
      :bill="taxInvoiceModal.bill"
      :shop-tax="shopTax"
      @close="taxInvoiceModal.open = false"
      @confirm="handleTaxInvoiceConfirm"
    />

    <offcanvas-filter
      v-if="offcanvasFilter.open"
      :sections="filterSections"
      :value="{
        status_ids: params.status_ids,
        source_ids: params.source_ids,
        branch_ids: params.branch_ids
      }"
      @apply="onApplyFilter"
      @close="offcanvasFilter.open = false"
    />
  </div>
</template>

<script lang="js">
import Vue3EasyDataTable from 'vue3-easy-data-table';
import PlaceholderContent from '@/plugins/PlaceholderContent.vue';
import AccessDenied from '@/plugins/AccessDenied.vue';
import PerPage from '@/plugins/PerPage.vue';
import ColumnToggle from '@/components/shared/ColumnToggle.vue';
import { loadVisibleColumns } from '@/utils/columnToggle';
import moment from 'moment';
import Popper from 'vue3-popper';
import DateRangePicker from 'vue3-daterange-picker';
import FieldsDef from './assets/template/datatable/FieldsDef';
import OffcanvasInfo from './OffcanvasInfo.vue';
import OffcanvasFilter from '@/components/shared/OffcanvasFilter.vue';
import ModalCollectPayment from './ModalCollectPayment.vue';
import ModalRefund from './ModalRefund.vue';
import ModalTaxInvoice from './ModalTaxInvoice.vue';
import { Bills } from '@/services/endpoint/bills.service';
import {
  buildPromptPayQrImage,
  buildReviewQrImage,
  buildReceiptDocDefinition,
  preparePrintConfig,
  printPdf
} from '@/utils/billReceipt';
import { isSupported as escposIsSupported, printCanvas, renderDocDefToCanvas, dotsForPaperWidth } from '@/utils/escposPrinter';
import { SOURCE_ICONS, STATUS_COLORS, TAB_ORDER, humanize } from './constants.js';

export default {
  components: {
    Vue3EasyDataTable,
    PlaceholderContent,
    AccessDenied,
    PerPage,
    ColumnToggle,
    Popper,
    DateRangePicker,
    OffcanvasInfo,
    OffcanvasFilter,
    ModalCollectPayment,
    ModalRefund,
    ModalTaxInvoice
  },
  data() {
    return {
      pageLoading: false,
      printingId: null, // bill id currently printing (per-row spinner on the print button)
      hasPermission: true,
      params: {
        limit: 10,
        page: 1,
        search: '',
        status_ids: [],
        source_ids: [],
        branch_ids: [],
        date_range: { startDate: null, endDate: null }
      },
      filter: {
        tab: 'all'
      },
      filters: {
        branches: [],
        payment_statuses: [],
        sources: []
      },
      // ค่าภาษีระดับร้าน (filter.shop_tax) — ใช้ตัดสินว่าออกใบกำกับเต็มรูปได้ไหม
      shopTax: { tax_policy: 'no_tax', vat_rate: 0, tax_id: '', tax_entity_name: '' },
      counts: { all: 0, unpaid: 0, partially_paid: 0, paid: 0 },
      datatable: {
        loading: false,
        headers: FieldsDef,
        items: [],
        total: 0,
        serverOptions: {
          page: 1,
          rowsPerPage: 10,
          sortBy: 'created_at',
          sortType: 'desc'
        }
      },
      offcanvasInfo:   { open: false, billId: null },
      collectModal:    { open: false, bill: null, paymentMethods: [] },
      refundModal:     { open: false, bill: null },
      taxInvoiceModal: { open: false, bill: null },
      offcanvasFilter: { open: false },
      visibleColumns: loadVisibleColumns('bills.columns', FieldsDef(t => t))
    };
  },
  computed: {
    shopIsVat() {
      return !!this.shopTax.tax_policy && this.shopTax.tax_policy !== 'no_tax';
    },
    allHeaders() {
      return this.datatable.headers(this.$t);
    },
    visibleHeaders() {
      return this.allHeaders.filter(h => !h.togglable || this.visibleColumns.includes(h.value));
    },
    dateRangeLocale() {
      return {
        firstDay: 1,
        format: 'yyyy-mm-dd',
        applyLabel:  this.$t('Apply'),
        cancelLabel: this.$t('Cancel'),
        clearable:   true,
        monthNames:  this.$storeIndex.monthNames(),
        daysOfWeek:  this.$storeIndex.daysOfWeek()
      };
    },
    sourceTypes() {
      return (this.filters.sources || []).map(s => ({
        value: s.text,
        label: s.name,
        icon: SOURCE_ICONS[s.text] || 'fa-solid fa-receipt'
      }));
    },
    statusTypes() {
      return (this.filters.payment_statuses || []).map(s => ({
        value: s.text,
        label: s.name,
        color: STATUS_COLORS[s.text] || 'secondary'
      }));
    },
    tabs() {
      return TAB_ORDER.map(v => {
        if (v === 'all') return { value: 'all', label: 'All', color: 'primary' };
        const s = this.statusTypes.find(x => x.value === v);
        return {
          value: v,
          label: s ? s.label : humanize(v),
          color: STATUS_COLORS[v] || 'primary'
        };
      });
    },
    activeFilterCount() {
      return this.params.status_ids.length
        + this.params.source_ids.length
        + this.params.branch_ids.length;
    },
    filterSections() {
      return [
        {
          key: 'status_ids',
          label: 'Status',
          icon: 'fa-circle-info',
          options: (this.filters.payment_statuses || []).map(s => ({ id: s.text, name: s.name }))
        },
        {
          key: 'source_ids',
          label: 'Source',
          icon: 'fa-store',
          options: (this.filters.sources || []).map(s => ({ id: s.text, name: s.name }))
        },
        {
          key: 'branch_ids',
          label: 'Branch',
          icon: 'fa-building',
          options: (this.filters.branches || []).map(b => ({ id: b.id, name: b.name }))
        }
      ];
    }
  },
  async mounted() {
    this.pageLoading = true;
    await this.fetchData();
    this.pageLoading = false;
  },
  methods: {
    buildQuery() {
      const p = { ...this.params };
      const query = {
        viewpoint: this.filter.tab || 'all',
        page: p.page,
        limit: p.limit
      };
      if (p.search) query.search = p.search;
      // filter เป็น checkbox เลือกได้หลายค่า — ส่งทั้ง array ($.param แตกเป็น payment_status[]=...)
      if (p.status_ids && p.status_ids.length) query.payment_status = [...p.status_ids];
      if (p.source_ids && p.source_ids.length) query.source = [...p.source_ids];
      if (p.branch_ids && p.branch_ids.length) {
        query.branch_ids = p.branch_ids;
      }
      if (p.date_range && p.date_range.startDate && p.date_range.endDate) {
        query.start = moment(p.date_range.startDate).format('YYYY-MM-DD');
        query.end   = moment(p.date_range.endDate).format('YYYY-MM-DD');
      }
      const { sortBy, sortType } = this.datatable.serverOptions;
      const sortableFields = ['created_at', 'closed_at'];
      if (sortBy && sortType && sortableFields.includes(sortBy)) {
        query.sort = sortBy + '|' + sortType;
      }
      return query;
    },
    async fetchData() {
      this.datatable.loading = true;
      try {
        const queryString = '?' + $.param(this.buildQuery());
        const res = await Bills.list(queryString);
        this.datatable.items = res.data.data || [];
        this.datatable.total = res.data.meta?.total || 0;
        if (res.data.viewpoint_cnt) {
          this.counts = { ...this.counts, ...res.data.viewpoint_cnt };
        }
        if (res.data.filter) {
          // ค่าภาษีเป็นของร้าน ไม่ใช่ของสาขา (api/docs/vat-compliance.md §2)
          this.shopTax = res.data.filter.shop_tax || this.shopTax;
          this.filters.branches = res.data.filter.branches || [];
          this.filters.payment_statuses = res.data.filter.payment_statuses || [];
          this.filters.sources = res.data.filter.sources || [];
        }
        this.hasPermission = true;
      } catch (err) {
        if (err?.response?.status === 403) {
          this.hasPermission = false;
          this.pageLoading = false;
        }
        // 401 handled by axios interceptor in router
      }
      this.datatable.loading = false;
    },
    setTab(v) {
      if (this.filter.tab === v) return;
      this.filter.tab = v;
      this.params.status_ids = [];
      this.datatable.serverOptions.page = 1;
      this.params.page = 1;
      this.fetchData();
    },
    setPerPage(perPage) {
      this.params.limit = perPage;
      this.$refs.dataTable.updateRowsPerPageActiveOption(perPage);
      this.fetchData();
    },
    onApplyFilter(payload) {
      this.params.status_ids = payload.status_ids || [];
      this.params.source_ids = payload.source_ids || [];
      this.params.branch_ids = payload.branch_ids || [];
      this.params.page = 1;
      this.datatable.serverOptions.page = 1;
      this.fetchData();
    },
    onDateRangeChange() {
      this.params.page = 1;
      this.datatable.serverOptions.page = 1;
      this.fetchData();
    },
    clearDateRange() {
      this.params.date_range = { startDate: null, endDate: null };
      this.onDateRangeChange();
    },
    formatDate(d) {
      return moment(d).format('YYYY-MM-DD');
    },
    formatDateTime(value) {
      return value ? moment(value).format('YYYY-MM-DD HH:mm') : '-';
    },
    toNumber(v) {
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : 0;
    },
    firstSource(item) {
      const s = item.sources;
      if (Array.isArray(s)) return s[0] || '';
      return s || '';
    },
    sourceIcon(v) {
      return SOURCE_ICONS[v] || 'fa-solid fa-receipt';
    },
    sourceLabel(v) {
      const s = this.sourceTypes.find(x => x.value === v);
      return s ? s.label : humanize(v);
    },
    statusLabel(v) {
      const s = this.statusTypes.find(x => x.value === v);
      return s ? s.label : humanize(v);
    },
    statusColor(v) {
      return STATUS_COLORS[v] || 'secondary';
    },
    // บิล platform: เงินจบบนแอปแล้ว — ห้ามรับเงิน/คืนเงิน/void จากหน้านี้
    // (settle อัตโนมัติตอน "เสร็จสิ้น" บน POS / ยกเลิกผ่านหน้า ออเดอร์ Platform)
    canCollect(item) {
      if (item.is_platform_bill) return false;
      return item.payment_status === 'unpaid' || item.payment_status === 'partially_paid';
    },
    canRefund(item) {
      const s = item.payment_status;
      if (item.voided_at || item.is_platform_bill) return false;
      return s === 'paid' || s === 'partially_refunded';
    },
    canVoid(item) {
      // Void only for unpaid bills (no money collected → no refund trail needed).
      // For partially_paid / paid bills, refund the payment first then void if needed.
      if (!item || item.voided_at || item.is_platform_bill) return false;
      return item.payment_status === 'unpaid';
    },
    hasTaxInvoice(item) {
      return !!item && item.tax_invoice_id !== '' && item.tax_invoice_id != null;
    },
    canIssueTaxInvoice(item) {
      if (!item || this.hasTaxInvoice(item)) return false;
      // ใบกำกับเต็มรูปต้องมีในแพ็ก (feature_tax_full) — ไม่มีก็ซ่อนปุ่ม ตัวบังคับจริงอยู่ที่ API
      if (!this.$hasFeature('feature_tax_full')) return false;
      return this.shopIsVat && item.payment_status === 'paid';
    },
    openBill(item) {
      this.offcanvasInfo.billId = item.id;
      this.offcanvasInfo.open = true;
    },
    closeOffcanvasInfo() {
      if (this.$refs.offcanvasInfoRef) {
        this.$refs.offcanvasInfoRef.hide();
      } else {
        this.offcanvasInfo.open = false;
      }
    },
    async loadAndOpen(billId, target) {
      try {
        const res = await Bills.info(billId);
        target.bill = res.data.data;
        target.open = true;
      } catch (err) {
        this.$toast.error(this.$t(err?.response?.data?.message) || this.$t('Failed'));
      }
    },
    async openCollect(item) {
      if (!this.canCollect(item)) return;
      this.closeOffcanvasInfo();
      await this.loadCollectPayment(item.id);
    },
    async openCollectById(billId) {
      if (!billId) return;
      this.closeOffcanvasInfo();
      await this.loadCollectPayment(billId);
    },
    async loadCollectPayment(billId) {
      try {
        const res = await Bills.getPayment(billId);
        this.collectModal.bill = res.data.data;
        this.collectModal.paymentMethods = res.data.payment_methods || [];
        this.collectModal.open = true;
      } catch (err) {
        this.$toast.error(this.$t(err?.response?.data?.message) || this.$t('Failed'));
      }
    },
    openRefund(item) {
      if (!this.canRefund(item)) return;
      this.closeOffcanvasInfo();
      this.loadAndOpen(item.id, this.refundModal);
    },
    openRefundById(billId) {
      if (!billId) return;
      this.closeOffcanvasInfo();
      this.loadAndOpen(billId, this.refundModal);
    },
    async quickPrint(item) {
      this.printBill(item.id, item.bill_no);
    },
    async quickPrintById(billId) {
      if (!billId) return;
      this.printBill(billId, '');
    },
    async printBill(id, billNo) {
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      // Reserve popup inside the user gesture so iOS Safari doesn't block it.
      // Skip it when WebUSB will handle the print (desktop Chromium) — no popup needed.
      const popup = (isMobile && !escposIsSupported()) ? window.open('', '_blank') : null;
      this.printingId = id;
      try {
        const res = await Bills.print(id);
        const apiBill = res.data?.data || {};
        const branchConfig = res.data?.filter?.branch_config || {};
        const configuration = await preparePrintConfig(branchConfig);
        // ค่าภาษีเป็นของร้าน ไม่ใช่ของสาขา (api/docs/vat-compliance.md §2)
        const shopTax = res.data?.filter?.shop_tax || {};
        const bill = this.toNormalizedBill(apiBill);

        // PromptPay QR: bills page doesn't fetch branch_payment_methods here,
        // so fall back to the previously-used promptpay payment on this bill
        // (if any) so the QR can still render when the flag is on.
        const lastPromptPay = (apiBill.payments || []).find(p => p && p.promptpay_no);
        const promptpayMethod = (configuration.show_qrcode_promptpay && lastPromptPay) ? lastPromptPay : null;
        const promptpayQrDataUrl = promptpayMethod
          ? await buildPromptPayQrImage(promptpayMethod, bill.grand_total)
          : '';
        const reviewQrDataUrl = (configuration.show_qrcode_review && configuration.review_url)
          ? await buildReviewQrImage(configuration.review_url)
          : '';

        const docOpts = {
          bill,
          configuration,
          shopTax,
          promptpayQrDataUrl,
          reviewQrDataUrl,
          promptpayMethod,
          fmt: (v) => this.$storeIndex.formatThaiCurrency(v),
          t: (s) => this.$t(s)
        };

        // Direct WebUSB raster print — cuts exactly under the receipt (no ~7cm
        // driver tail). Build without short-page padding. On unsupported/failure,
        // fall back to the PDF print path below.
        if (escposIsSupported()) {
          try {
            const canvas = await renderDocDefToCanvas(
              buildReceiptDocDefinition({ ...docOpts, padShortPage: false }),
              { widthPx: dotsForPaperWidth(configuration.bill_paper_width) }
            );
            await printCanvas(canvas);
            return;
          } catch (e) {
            console.warn('WebUSB bill print failed; falling back to PDF:', e);
          }
        }
        await printPdf(buildReceiptDocDefinition(docOpts), { popup });
        // this.$toast.info('<b>' + this.$t('Printing bill') + '</b> ' + (billNo || apiBill.bill_no || ''));
      } catch (err) {
        if (popup) popup.close();
        this.$toast.error(this.$t(err?.response?.data?.message) || this.$t('Failed'));
      } finally {
        this.printingId = null;
      }
    },
    // Map API bill payload → normalized shape expected by billReceipt util.
    toNormalizedBill(api) {
      const toNum = (v) => {
        const n = parseFloat(v);
        return Number.isFinite(n) ? n : 0;
      };
      return {
        bill_no:             api.bill_no,
        // '' unless the API says this receipt is the abbreviated tax invoice
        abbreviated_tax_invoice_no: api.abbreviated_tax_invoice_no || '',
        // ยังไม่จ่าย = ใบแจ้งรายการ ไม่ใช่เอกสารภาษี (billReceipt.js เปลี่ยนหัวบิลให้)
        payment_status: api.payment_status || '',
        // เลขประจำเครื่องจากสรรพากร + ช่องทางขาย — ต้องพิมพ์บนใบกำกับอย่างย่อ
        // (api/docs/vat-compliance.md D10) ว่างได้ทั้งคู่
        pos_device_code: api.pos_device_code || '',
        delivery_platform: api.delivery_platform || '',
        // วันที่ปิดการขาย — ใช้เป็นวันที่บนใบกำกับภาษี (พิมพ์ซ้ำต้องได้วันเดิม)
        closed_at:           api.closed_at || '',
        table_name:          api.table_name,
        customer_name:       api.customer_name,
        customer_phone:      api.customer_phone,
        tax_policy:          api.tax_policy,
        subtotal:            toNum(api.sub_total),
        discount:            toNum(api.discount),
        shipping_price:      toNum(api.shipping_price),
        service_charge:      toNum(api.service_charge_amount),
        service_charge_rate: toNum(api.service_charge_rate),
        total_before_vat:    toNum(api.total_before_vat),
        vat:                 toNum(api.total_vat),
        vat_rate:            toNum(api.vat_rate),
        grand_total:         toNum(api.total),
        items: (api.items || []).map(it => ({
          name:               it.name,
          unit_price:         toNum(it.unit_price),
          quantity:           toNum(it.quantity),
          cancelled_quantity: toNum(it.cancelled_quantity),
          subtotal:           toNum(it.subtotal),
          discount:           toNum(it.discount),
          total:              toNum(it.total),
          remark:             it.remark || '',
          is_reward:          !!it.is_reward,
          modifiers: (it.options || []).map(o => ({ name: o.name, price: parseFloat(o.price) || 0 }))
        }))
      };
    },
    quickVoid(item) {
      if (!this.canVoid(item)) return;
      this.confirmVoid(item.id, item.bill_no);
    },
    quickVoidById(billId) {
      if (!billId) return;
      this.confirmVoid(billId, '');
    },
    confirmVoid(id, billNo) {
      this.$swal({
        icon: 'warning',
        iconColor: '#FF7043',
        title: this.$t('Void Bill') + ' ' + (billNo ? '#' + billNo : '') + '?',
        html: this.$t('This action cannot be undone.'),
        input: 'text',
        inputPlaceholder: this.$t('Reason') + '...',
        inputValidator: v => !v ? this.$t('Reason') + ' ' + this.$t('is required') : null,
        showCancelButton: true,
        cancelButtonText: this.$t('Cancel'),
        confirmButtonText: this.$t('Void'),
        buttonsStyling: false,
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-secondary me-2'
        },
        reverseButtons: true
      }).then(async res => {
        if (!res.isConfirmed) return;
        try {
          await Bills.void(id, { void_reason: res.value });
          this.closeOffcanvasInfo();
          this.$toast.success('<b>' + this.$t('Voided') + '</b> ' + (billNo ? '#' + billNo : ''));
          this.fetchData();
        } catch (err) {
          this.$toast.error(this.$t(err?.response?.data?.message) || this.$t('Failed'));
        }
      });
    },
    async handleCollectConfirm(payload) {
      const bill = this.collectModal.bill;
      if (!bill) return;
      try {
        await Bills.payment(bill.id, { payment: payload.payment });
        if (this.$refs.collectModalRef) this.$refs.collectModalRef.hide();
        this.$toast.success('<b>' + this.$t('Payment received') + '</b> #' + bill.bill_no);
        this.fetchData();
      } catch (err) {
        this.$toast.error(this.$t(err?.response?.data?.message) || this.$t('Failed'));
        if (this.$refs.collectModalRef) this.$refs.collectModalRef.stopSubmitting();
      }
    },
    async openTaxInvoice(item) {
      if (!this.canIssueTaxInvoice(item)) return;
      this.closeOffcanvasInfo();
      // Fetch full detail so the modal has customer_id + branch tax fields.
      await this.loadAndOpen(item.id, this.taxInvoiceModal);
    },
    async openTaxInvoiceById(billId) {
      if (!billId) return;
      this.closeOffcanvasInfo();
      await this.loadAndOpen(billId, this.taxInvoiceModal);
    },
    async handleTaxInvoiceConfirm(payload) {
      const bill = this.taxInvoiceModal.bill;
      if (!bill) return;
      try {
        await Bills.taxInvoice(bill.id, payload);
        if (this.$refs.taxInvoiceModalRef) this.$refs.taxInvoiceModalRef.hide();
        this.$toast.success('<b>' + this.$t('Tax invoice issued') + '</b> #' + bill.bill_no);
        this.fetchData();
      } catch (err) {
        this.$toast.error(this.$t(err?.response?.data?.message) || this.$t('Failed'));
        if (this.$refs.taxInvoiceModalRef) this.$refs.taxInvoiceModalRef.stopSubmitting();
      }
    },
    quickVoidTaxInvoice(item) {
      if (!this.hasTaxInvoice(item)) return;
      this.confirmVoidTaxInvoice(item.id, item.tax_invoice_no);
    },
    quickVoidTaxInvoiceById(billId) {
      if (!billId) return;
      this.confirmVoidTaxInvoice(billId, '');
    },
    confirmVoidTaxInvoice(billId, invoiceNo) {
      this.$swal({
        icon: 'warning',
        iconColor: '#FF7043',
        title: this.$t('Void Tax Invoice') + (invoiceNo ? ' ' + invoiceNo : '') + '?',
        html: this.$t('The invoice number cannot be reused. A new number will be assigned if re-issued.'),
        input: 'text',
        inputPlaceholder: this.$t('Reason') + '...',
        inputValidator: v => !v ? this.$t('Reason') + ' ' + this.$t('is required') : null,
        showCancelButton: true,
        cancelButtonText: this.$t('Cancel'),
        confirmButtonText: this.$t('Void'),
        buttonsStyling: false,
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-secondary me-2'
        },
        reverseButtons: true
      }).then(async res => {
        if (!res.isConfirmed) return;
        try {
          await Bills.voidTaxInvoice(billId, { void_reason: res.value });
          this.closeOffcanvasInfo();
          this.$toast.success('<b>' + this.$t('Tax invoice voided') + '</b> ' + (invoiceNo || ''));
          this.fetchData();
        } catch (err) {
          this.$toast.error(this.$t(err?.response?.data?.message) || this.$t('Failed'));
        }
      });
    },
    async handleRefundConfirm(payload) {
      const bill = this.refundModal.bill;
      if (!bill) return;
      try {
        await Bills.refund(bill.id, {
          refund_reason: payload.refund_reason,
          items: payload.items
        });
        if (this.$refs.refundModalRef) this.$refs.refundModalRef.hide();
        this.$toast.success('<b>' + this.$t('Refunded') + '</b> #' + bill.bill_no);
        this.fetchData();
      } catch (err) {
        this.$toast.error(this.$t(err?.response?.data?.message) || this.$t('Failed'));
        if (this.$refs.refundModalRef) this.$refs.refundModalRef.stopSubmitting();
      }
    }
  },
  watch: {
    // DateRangePicker fires v-model updates after auto-apply; refetch when the
    // range actually changes (skip the initial empty → empty no-op).
    'params.date_range': {
      deep: true,
      handler(val, old) {
        const a = (val && val.startDate) ? String(val.startDate) : '';
        const b = (val && val.endDate)   ? String(val.endDate)   : '';
        const oa = (old && old.startDate) ? String(old.startDate) : '';
        const ob = (old && old.endDate)   ? String(old.endDate)   : '';
        if (a === oa && b === ob) return;
        this.onDateRangeChange();
      }
    },
    'datatable.serverOptions'(nval) {
      this.params.page = nval.page;
      this.params.limit = nval.rowsPerPage;
      this.fetchData();
    }
  }
};
</script>

<style scoped>
.hide-scrollbar::-webkit-scrollbar { display: none; }
.hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
</style>
